import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import type { SearchData, SearchMode, Session } from "../types";
import { SEARCH_RESULTS_PAGE_SIZE } from "../constants/searchSessions";
import type { ProceedingLogs } from "../types/searchSessions";
import { getResult } from "../api/searchApis";
import logger from "../utils/logger";

export const validateSessionSearch = (
  searchData: SearchData,
  searchMode: SearchMode,
) => {
  switch (searchMode) {
    case "cnr":
      return Boolean(searchData.cnr && searchData.date);

    case "highCourt":
      return Boolean(
        searchData.highcourt &&
        searchData.bench &&
        searchData.benchRoom &&
        searchData.date,
      );

    case "district":
      return Boolean(
        searchData.state &&
        searchData.district &&
        searchData.courtComplex &&
        searchData.courtName &&
        searchData.date,
      );

    default:
      return false;
  }
};

export const buildSessionSearchPayload = (
  searchData: SearchData,
  searchMode: SearchMode,
  page: number,
) => ({
  cnr: searchMode === "cnr" ? searchData.cnr : undefined,
  date: searchData.date,
  type: searchMode,
  courtName: searchMode === "district" ? searchData.courtName : undefined,
  bench: searchMode === "highCourt" ? searchData.bench : undefined,
  benchRoom: searchMode === "highCourt" ? searchData.benchRoom : undefined,
  status: searchData.status,
  page,
  limit: SEARCH_RESULTS_PAGE_SIZE,
});

export const useSessionSearch = (
  searchData: SearchData,
  searchMode: SearchMode,
) => {
  const [results, setResults] = useState<Session[]>([]);
  const [proceedingData, setProceedingData] = useState<ProceedingLogs>();
  const [isSearching, setIsSearching] = useState(false);
  const [isPaginating, setIsPaginating] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const resetSearchResults = useCallback(() => {
    setResults([]);
    setProceedingData(undefined);
    setPage(1);
    setTotal(0);
    setTotalPages(1);
  }, []);

  const handleSearch = useCallback(
    async (pageNum: number = 1, fromPagination = false) => {
      if (isSearching || isPaginating) return;

      if (!validateSessionSearch(searchData, searchMode)) {
        toast.error("Please select all mandatory fields");
        return;
      }

      setPage(pageNum);

      fromPagination ? setIsPaginating(true) : setIsSearching(true);

      try {
        const res = await getResult(
          buildSessionSearchPayload(searchData, searchMode, pageNum),
        );

        logger.log("Search Response", res);

        const data = res?.data;

        if (!data?.status) {
          toast.error(data?.message || "Search failed");
          resetSearchResults();
          return;
        }

        setResults(data.data || []);
        setProceedingData(data.proceedingLogs);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Data not found");
        resetSearchResults();
      } finally {
        setIsSearching(false);
        setIsPaginating(false);
      }
    },
    [isPaginating, isSearching, resetSearchResults, searchData, searchMode],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      handleSearch(newPage, true);
    },
    [handleSearch],
  );

  return {
    results,
    setResults,
    proceedingData,
    isSearching,
    isPaginating,
    page,
    totalPages,
    total,
    handleSearch,
    handlePageChange,
    resetSearchResults,
  };
};
